import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TrendingDown, Minus } from 'lucide-react';

const fmtM = (val) => {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

// Exposure reduces proportionally as open risks are closed.
// EAL at horizon = baseEAL × (remaining open / current open)
function projectedEAL(baseEAL, openCount, velocityPerMonth, monthsFactor) {
  if (openCount <= 0) return baseEAL;
  const remaining = Math.max(0, openCount - velocityPerMonth * monthsFactor);
  return baseEAL * (remaining / openCount);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow px-3 py-2 text-xs">
      <div className="font-semibold text-gray-700 mb-1">{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }} className="font-semibold">
          {fmtM(p.value * 1_000_000)} — {p.name}
        </div>
      ))}
    </div>
  );
};

/**
 * RansomwareForwardCurveChart
 *
 * Contrasts two forward scenarios from today's EAL, both projected forward:
 *   - "Do nothing"    — flat at today's EAL. Risk persists; exposure never retires.
 *   - "Close 2/month" — EAL declines as open risks close at 2 remediations/month.
 *
 * The gap between the lines at each horizon is the message: what a steady
 * remediation pace buys you versus standing still.
 *
 * Props:
 *   predictive  — { openCount, velocityPerMonth }  (baseEAL/eal45… no longer used here)
 *   currentEAL  — today's deterministic EAL (the anchor both scenarios start from)
 *   currentP90  — MC P90 fallback when currentEAL is absent
 */
export default function RansomwareForwardCurveChart({ predictive, currentEAL, currentP90 }) {
  // anchor = EAL (matches the headline metric in the MC section above)
  const anchor    = Number(currentEAL ?? currentP90 ?? 0);
  // openCount is null when trajectory data is unavailable, 0 when nothing is open.
  const openCountRaw = predictive?.openCount;
  const openCount    = Number(openCountRaw ?? 0);
  const hasOpenData  = openCountRaw != null;
  const velocity     = Number(predictive?.velocityPerMonth ?? 0);

  if (anchor <= 0) return null;

  const TARGET_VELOCITY = 2;
  // Can only project a decline when we know there are open risks to close.
  const canProject = hasOpenData && openCount > 0;

  // ── Scenario 1: Do nothing — flat, risk perpetuates ─────────────────────────
  const doNothing = [anchor, anchor, anchor, anchor];

  // ── Scenario 2: Close 2/month — proportional burndown of open risks ─────────
  const doTarget = canProject ? [
    anchor,
    projectedEAL(anchor, openCount, TARGET_VELOCITY, 1.5),
    projectedEAL(anchor, openCount, TARGET_VELOCITY, 3.0),
    projectedEAL(anchor, openCount, TARGET_VELOCITY, 6.0),
  ] : null;

  const LABELS = ['Today', '+45d', '+90d', '+180d'];
  const chartData = LABELS.map((label, i) => ({
    label,
    nothing: doNothing[i] / 1_000_000,
    target:  doTarget ? doTarget[i] / 1_000_000 : undefined,
  }));

  // Y-axis domain — do-nothing sits at the top (anchor), target declines below it.
  const allVals = [...doNothing, ...(doTarget ?? [])].filter(v => v > 0);
  const dataMin = Math.min(...allVals) / 1_000_000;
  const dataMax = Math.max(...allVals) / 1_000_000;
  const pad     = (dataMax - dataMin) * 0.20 || dataMax * 0.05;
  const yDomain = [Math.max(0, dataMin - pad), dataMax];

  // Headline: exposure avoided at 90d by closing 2/mo instead of doing nothing.
  const target90     = doTarget ? doTarget[2] : anchor;
  const avoided90    = anchor - target90;
  const avoidedPct90 = anchor > 0 ? (avoided90 / anchor) * 100 : 0;
  const isImproving  = avoided90 > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isImproving
            ? <TrendingDown className="w-4 h-4 text-green-500" />
            : <Minus className="w-4 h-4 text-gray-400" />}
          <span className="text-sm font-semibold text-gray-800">Forward Curve</span>
          {predictive?.velocityPerMonth != null && (
            <span
              title={`${velocity} remediation${velocity !== 1 ? 's' : ''} completed in the last 30 days`}
              className="text-xs text-gray-400 cursor-help"
            >
              you're at {velocity}/mo
            </span>
          )}
        </div>

        <div className="text-xs tabular-nums font-semibold text-right">
          {isImproving ? (
            <span className="text-green-600">
              Close {TARGET_VELOCITY}/mo → ↓ {fmtM(avoided90)} avoided at 90d (−{avoidedPct90.toFixed(0)}%)
            </span>
          ) : canProject ? (
            <span className="text-gray-500">→ 2/mo clears open risks before 90d</span>
          ) : hasOpenData ? (
            <span className="text-gray-500">→ no open risks to remediate</span>
          ) : (
            <span className="text-gray-500">→ remediation data unavailable</span>
          )}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis
            tick={{ fontSize: 10 }}
            tickFormatter={v => `$${v.toFixed(1)}M`}
            width={55}
            domain={yDomain}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Do nothing — flat, risk perpetuates */}
          <Line
            type="monotone"
            dataKey="nothing"
            name="Do nothing"
            stroke="#d97706"
            strokeWidth={2}
            dot={{ r: 3, fill: '#d97706', stroke: '#fff', strokeWidth: 1.5 }}
            activeDot={{ r: 4 }}
          />

          {/* Close 2/month — declining */}
          {doTarget && (
            <Line
              type="monotone"
              dataKey="target"
              name={`Close ${TARGET_VELOCITY}/mo`}
              stroke="#22c55e"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ r: 3, fill: '#22c55e', stroke: '#fff', strokeWidth: 1.5 }}
              activeDot={{ r: 4 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Footer */}
      <div className="flex items-center justify-between mt-1 text-xs text-gray-400">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-0.5 bg-amber-500 rounded" />
            Do nothing
          </span>
          {doTarget && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-4 border-t border-dashed border-green-400" style={{ borderTopWidth: 1.5 }} />
              Close {TARGET_VELOCITY}/mo
            </span>
          )}
        </div>
        <span>Today = EAL · update remediations to refresh.</span>
      </div>
    </div>
  );
}
